import AutosaveSuccess from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    Table,
    TableBody,
    TableHead,
    TableRow,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import SaveAndExitButton from "../../../common/js/components/SaveAndExitButton";
import { LeftChevron } from "../../../common/js/icons";
import ComplianceRulesByDocumentTypeContainer from "./ComplianceRulesByDocumentTypeContainer";
import operations from "./ducks/operations";

const PageToolbar = styled(Grid)(() => ({
    marginBottom: "20px",
    marginTop: "20px",
}));

const GoBackLink = styled("span")({
    textDecoration: "none",
    "&:hover": {
        cursor: "pointer",
        textDecoration: "none",
    },
});

const DeleteButton = styled(Button)(() => ({
    backgroundColor: red[500],
    color: "white",
    "&:hover": {
        backgroundColor: red[800],
    },
}));

const LoadingSpinner = styled("span")(() => ({
    position: "absolute",
    left: "50%",
    top: "60%",
    transform: "translate(-50%, -50%)",
    marginLeft: "50px",
}));

const ComplianceRules = styled(AccordionDetails)(() => ({
    paddingTop: "0px",
}));

const DocumentTypeAdmin = (props) => {
    const { errors, history, documentTypeId, handleSaveAll, location, handleDeleteDocumentType } = props;

    const [documentType, setDocumentType] = useState({});
    const [parentEntityTypesList, setParentEntityTypesList] = useState([]);
    const [parentEntityTypesToOptionsMap, setParentEntityTypesToOptionsMap] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [knownDocument, setKnownDocument] = useState(true);
    const [newDocumentName, setNewDocumentName] = useState("");
    const [isExisting, setIsExisting] = useState(true);

    const subHeaderText = isExisting ? "Edit Document" : "Create Document";
    const deleteCancelText = isExisting ? "Delete" : "Cancel";
    const parentEntityTypeKey = get(documentType, "parentEntityType.parentEntityTypeKey", "");

    const loadDTSRData = async () => {
        const parentEntityTypes = await operations.getAllParentEntityTypes();

        let docTypes = {};
        if (documentTypeId) {
            docTypes = await operations.getDocumentType(documentTypeId);
            setDocumentType(docTypes);
        } else {
            docTypes = { multipleAllowed: false };
            setDocumentType(docTypes);
            setIsExisting(false);
        }

        // Set parent entity types list first
        setParentEntityTypesList(parentEntityTypes);

        // Create options map using the fetched data directly, not the state
        const optionsMap = [];
        optionsMap.push({
            value: "",
            label: "Select A Parent Type",
            disabled: true,
        });
        parentEntityTypes.forEach((entity) => {
            optionsMap.push({
                value: entity["parentEntityTypeKey"],
                label: entity["name"],
                disabled: false,
            });
        });
        setParentEntityTypesToOptionsMap(optionsMap);

        setNewDocumentName(docTypes.name);

        if (docTypes === "ERROR") {
            setKnownDocument(false);
        }
        setDataLoaded(true);
    };

    useEffect(() => {
        loadDTSRData();
    }, []);

    const saveAllValues = async (shouldExit) => {
        const updatedDocumentType = {};

        updatedDocumentType.name = newDocumentName;
        updatedDocumentType.description = get(documentType, "description");
        updatedDocumentType.multipleAllowed = get(documentType, "multipleAllowed");
        updatedDocumentType.parentEntityTypeKey = get(documentType, "parentEntityType.parentEntityTypeKey");
        const result = await handleSaveAll(documentTypeId, updatedDocumentType);
        if (result.success && shouldExit) {
            history.push("/document-type");
        }
    };

    const handleDeleteCancelDocumentType = async () => {
        const currentPath = location.pathname;
        if (currentPath === "/document-type/create") {
            history.push("/document-type");
        } else {
            const result = await handleDeleteDocumentType(documentTypeId);
            if (result.success) {
                history.push("/document-type");
            }
        }
    };

    const handleDocumentNameChange = (v) => {
        setNewDocumentName(v);
    };

    const handleInputChange = (k, v) => {
        setDocumentType((prevState) => ({
            ...prevState,
            [k]: v,
        }));
    };

    const changeParentEntity = (e) => {
        let newParentEntity;

        newParentEntity = documentType.parentEntity;
        parentEntityTypesList.forEach((entity) => {
            if (entity.parentEntityTypeKey === Number(e.target.value)) {
                newParentEntity = entity;
            }
        });
        handleInputChange(e.target.id, newParentEntity);
    };

    return (
        <React.Fragment>
            <PageToolbar container justifyContent="space-between" spacing={16}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <GoBackLink onClick={() => history.goBack()}>
                        <LeftChevron />
                    </GoBackLink>
                    <div>
                        <h2 className="page-title">{dataLoaded ? subHeaderText : "Document"}</h2>
                        <p className="page-subtitle">{get(documentType, "name", "")}</p>
                    </div>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    {parentEntityTypeKey.length !== 0 && (
                        <Link to={`/parent-type/${parentEntityTypeKey}`} style={{ textDecoration: "none" }}>
                            <Button variant="contained" color="secondary">
                                Edit Rules
                            </Button>
                        </Link>
                    )}
                    <SaveAndExitButton
                        onSave={() => {
                            saveAllValues(false);
                        }}
                        onSaveAndExit={() => {
                            saveAllValues(true);
                        }}
                    />
                    {dataLoaded ? (
                        <DeleteButton
                            variant="contained"
                            onClick={() => {
                                handleDeleteCancelDocumentType();
                            }}
                        >
                            {deleteCancelText}
                        </DeleteButton>
                    ) : (
                        <LoadingSpinner>
                            <CircularProgress color="primary" size={15} />
                        </LoadingSpinner>
                    )}
                </div>
            </PageToolbar>
            {knownDocument ? (
                <Grid container>
                    <Table>
                        <TableBody style={{ flexDirection: "column" }}>
                            <TableRow>
                                <Accordion defaultExpanded style={{ marginBottom: "10px" }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <AutosaveSuccess className="waterloo" /> Document Information
                                    </AccordionSummary>
                                    <Divider />
                                    <AccordionDetails>
                                        <Table>
                                            <TableHead>
                                                <BSInput
                                                    id="name"
                                                    label="Name"
                                                    placeholder="Enter document name..."
                                                    value={newDocumentName}
                                                    onChange={(e) => {
                                                        handleDocumentNameChange(e.target.value);
                                                    }}
                                                    errorText={"Required"}
                                                    error={get(errors, "name", "")}
                                                />
                                                <BSInput
                                                    id="description"
                                                    label="Description"
                                                    placeholder="Enter document description..."
                                                    value={documentType.description}
                                                    onChange={(e) => {
                                                        handleInputChange(e.target.id, e.target.value);
                                                    }}
                                                />
                                                <BSSelect
                                                    id="parentEntityType"
                                                    label="Parent Type"
                                                    value={documentType?.parentEntityType?.parentEntityTypeKey || ""}
                                                    onChange={(e) => {
                                                        changeParentEntity(e);
                                                    }}
                                                    options={parentEntityTypesToOptionsMap}
                                                    disabled={documentTypeId !== undefined}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={documentType.multipleAllowed ? true : false}
                                                            onChange={(e) =>
                                                                handleInputChange(e.target.value, e.target.checked)
                                                            }
                                                            value="multipleAllowed"
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Multiple Attachments Allowed"
                                                />
                                            </TableHead>
                                        </Table>
                                    </AccordionDetails>
                                </Accordion>
                                {documentTypeId !== undefined ? (
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <AutosaveSuccess className="waterloo" />
                                            Compliance Rules Impacting Document Type
                                        </AccordionSummary>
                                        <Divider />
                                        <ComplianceRules>
                                            <ComplianceRulesByDocumentTypeContainer {...props} />
                                        </ComplianceRules>
                                    </Accordion>
                                ) : null}
                            </TableRow>
                        </TableBody>
                    </Table>
                </Grid>
            ) : (
                <Grid container style={{ width: "70%", marginTop: "20px" }}>
                    <p>Error: Document could not be found.</p>
                </Grid>
            )}
        </React.Fragment>
    );
};

export default withRouter(DocumentTypeAdmin);
